import React from 'react';

import ExpandedRunCurrentRows from './ExpandedRunCurrentRows';
import ExpandedRunHistoryRows from './ExpandedRunHistoryRows';

const ExpandedRunDetails = ({ controller, run, runRowKey, attempted, successful, skipped }) => {
  const {
    getRunConfidencePercent,
    setRunConfidencePercent,
    getRunTokenTotals,
    getRunCacheStats,
    toFiniteCount,
    formatTokenCount,
    withRagPromptPreview,
    loadingRunDetailsById,
    getFilteredItemEntries,
  } = controller;

  const runConfidencePercent = getRunConfidencePercent(run, runRowKey);
  const runMinConfidenceThreshold = runConfidencePercent / 100;
  const runTokenTotals = getRunTokenTotals(run);
  const runCacheStats = getRunCacheStats(run);
  const runId = String(run?.run_id || '').trim();
  const isRunDetailsLoading = Boolean(loadingRunDetailsById[runId]);
  const hasLoadedRunResults = Array.isArray(run?.results);
  const isRunDetailsPending = isRunDetailsLoading && !hasLoadedRunResults;

  return (
    <div className="space-y-3">
      <div className="rounded border border-ai/20 bg-ai/10 px-2.5 py-2 text-xs text-ai">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold uppercase tracking-wide text-[10px]">Min Conf</span>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={runConfidencePercent}
            onChange={(event) => setRunConfidencePercent(run, runRowKey, Number(event.target.value))}
            className="flex-1 min-w-[160px] max-w-[280px] accent-indigo-600"
          />
          <span className="w-12 shrink-0 text-right font-semibold tabular-nums">{runConfidencePercent}%</span>
          <button
            type="button"
            onClick={() => setRunConfidencePercent(run, runRowKey, 0)}
            disabled={runConfidencePercent === 0}
            className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${runConfidencePercent === 0 ? 'border-ai/20 bg-ai/10 text-ai/60 cursor-not-allowed' : 'border-ai/40 bg-surface text-ai hover:bg-ai/10'}`}
          >
            Reset
          </button>
        </div>
      </div>

      {isRunDetailsPending ? (
        <div className="rounded border border-hairline bg-surface p-4 text-sm text-muted italic">
          Loading run details...
        </div>
      ) : (
        <>
          <div className={`rounded border p-2 text-xs ${skipped > 0 ? 'bg-warn/10 border-warn/20 text-warn' : 'bg-positive/10 border-positive/20 text-positive'}`}>
            Data points: {successful ?? attempted ?? 0}
            {attempted !== null ? ` successful out of ${attempted}` : ''}
            {skipped > 0 ? ` (${skipped} skipped)` : ''}
          </div>
          <div className="rounded border border-hairline bg-surface p-2 text-xs text-ink">
            <span className="mr-2">Tokens:</span>
            <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${toFiniteCount(runTokenTotals.input) !== null ? 'bg-teal/10 text-teal' : 'bg-surface-2 text-muted'}`}>
              In {formatTokenCount(runTokenTotals.input)}
            </span>
            <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ml-1 ${toFiniteCount(runTokenTotals.output) !== null ? 'bg-ai/10 text-ai' : 'bg-surface-2 text-muted'}`}>
              Out {formatTokenCount(runTokenTotals.output)}
            </span>
            <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ml-1 ${toFiniteCount(runTokenTotals.total) !== null ? 'bg-positive/10 text-positive' : 'bg-surface-2 text-muted'}`}>
              Tot {formatTokenCount(runTokenTotals.total)}
            </span>
          </div>
          <div className="rounded border border-teal/20 bg-teal/10 p-2 text-xs text-teal">
            Cache hits: {runCacheStats.count}/{runCacheStats.total}
            {runCacheStats.total > 0 ? ` (${(runCacheStats.rate * 100).toFixed(1)}%)` : ''}
          </div>
          {Array.isArray(run.skipped_lots) && run.skipped_lots.length > 0 && (
            <div className="rounded border border-warn/20 bg-warn/10 p-2 text-xs text-warn">
              Skipped lots: {run.skipped_lots.slice(0, 12).join(', ')}
              {run.skipped_lots.length > 12 ? `, +${run.skipped_lots.length - 12} more` : ''}
            </div>
          )}
          <div className="bg-deep rounded border border-white/10 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-white/60 mb-2">
              Prompt Template
            </div>
            <pre className="m-0 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-white font-mono max-h-64 overflow-y-auto">
              {withRagPromptPreview(
                run.prompt_template || '',
                run.use_ebay_rag,
                run.use_notes_rag,
                run.assume_hidden_items,
              ) || 'Prompt template was not stored for this run.'}
            </pre>
          </div>
          <div className="bg-surface rounded border border-hairline shadow-inner overflow-x-auto">
            <table className="w-full text-left text-[11px] table-fixed">
              <thead>
                <tr className="bg-surface-2">
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[3%]">Lot #</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[7%]">Image</th>
                  <th
                    className="px-1.5 py-1 border-r border-b border-hairline w-[10.5%]"
                    title="What the lot's manifest line says it is — the brand and product each answer is graded against."
                  >
                    Manifest (ground truth)
                  </th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[12%]">AI Item Name</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[5%]">AI Est.</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[5%]">AI Est. Sum</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[5%]">Actual</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[11%]">AI Explanation</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[8%]">Subitem Notes</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[8%]">Item Notes</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[4.5%]">Acc.</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[4.5%]">Dur.</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[4.5%]">Src</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[4%]">In Tok</th>
                  <th className="px-1.5 py-1 border-r border-b border-hairline w-[4%]">Out Tok</th>
                  <th className="px-1.5 py-1 border-b border-hairline w-[4%]">Cost</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(run?.results) ? (
                  <>
                    {run.results.map((res, ridx) => {
                      const resultItemEntries = getFilteredItemEntries(res?.items, runMinConfidenceThreshold);
                      if (resultItemEntries.length === 0) return null;
                      return (
                        <React.Fragment key={ridx}>
                          <ExpandedRunCurrentRows
                            controller={controller}
                            run={run}
                            res={res}
                            ridx={ridx}
                            runMinConfidenceThreshold={runMinConfidenceThreshold}
                          />
                          {Array.isArray(res.rerun_history) && res.rerun_history.length > 0 &&
                            [...res.rerun_history].reverse().map((hist, hidx) => (
                              <ExpandedRunHistoryRows
                                key={`${ridx}-hist-${hidx}`}
                                controller={controller}
                                run={run}
                                res={res}
                                ridx={ridx}
                                hist={hist}
                                hidx={hidx}
                                runMinConfidenceThreshold={runMinConfidenceThreshold}
                              />
                            ))}
                        </React.Fragment>
                      );
                    })}
                    {!isRunDetailsLoading && run.results.length === 0 && (
                      <tr>
                        <td colSpan="16" className="px-2 py-4 text-center text-muted italic">
                          No lot-level details available for this run.
                        </td>
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan="16" className="px-2 py-4 text-center text-muted italic">
                      {isRunDetailsLoading ? 'Loading run details...' : 'No lot-level details available for this run.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpandedRunDetails;
